import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle } from 'lucide-react';
import api from '../utils/api';

const PROVIDER_NAMES = { github: 'GitHub', facebook: 'Facebook', twitter: 'Twitter/X' };

const SETUP_URLS = {
  github: 'https://github.com/settings/developers',
  facebook: 'https://developers.facebook.com/apps',
  twitter: 'https://developer.twitter.com/en/portal/projects-and-apps',
};

const SocialAuthCallback = () => {
  const navigate = useNavigate();
  const { provider } = useParams();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  const providerName = PROVIDER_NAMES[provider] || provider;
  const setupUrl = SETUP_URLS[provider] || '';

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const errorParam = params.get('error') || params.get('error_description');

        if (errorParam) {
          setError(`${providerName} login failed: ${errorParam}`);
          return;
        }

        if (!code) {
          setError(`No authorization code received from ${providerName}.`);
          return;
        }

        const redirectUri = `${process.env.REACT_APP_APP_URL || window.location.origin}/auth/callback/${provider}`;
        const body = { code, redirect_uri: redirectUri };

        if (provider === 'twitter') {
          const codeVerifier = sessionStorage.getItem('twitter_code_verifier');
          if (!codeVerifier) {
            setError('Twitter PKCE verification failed. Please try logging in again.');
            return;
          }
          body.code_verifier = codeVerifier;
          sessionStorage.removeItem('twitter_code_verifier');
          sessionStorage.removeItem('twitter_oauth_state');
        }

        const response = await api.post(`/auth/${provider}/callback`, body);

        if (response.data.success) {
          const userData = response.data.user;
          const token = response.data.token;
          if (token) localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('isAuthenticated', 'true');
          setUser(userData);
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          setError(`${providerName} authentication failed. Please try again.`);
        }
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        if (detail.includes('redirect_uri') || detail.includes('redirect')) {
          setError(`Redirect URI mismatch for ${providerName}. You need to add the callback URL to your ${providerName} OAuth app settings.`);
        } else {
          setError(`${providerName} authentication failed: ${detail}`);
        }
      }
    };

    processAuth();
  }, [location.search, navigate, setUser, provider, providerName]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-lg mx-auto px-6" data-testid="oauth-error">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-3">{providerName} Login Failed</h2>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <div className="p-3 bg-muted rounded-lg border border-input mb-4 text-left">
            <p className="text-xs text-muted-foreground mb-2">Add this callback URL to your {providerName} app:</p>
            <code className="text-xs text-blue-400 break-all block">{process.env.REACT_APP_APP_URL || window.location.origin}/auth/callback/{provider}</code>
            {setupUrl && (
              <a href={setupUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-400 hover:text-violet-300 mt-2 inline-block">
                Open {providerName} Developer Console →
              </a>
            )}
          </div>
          <button onClick={() => navigate('/', { replace: true })} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-all" data-testid="oauth-error-back-btn">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-foreground mb-2">Signing you in...</h2>
        <p className="text-muted-foreground">Completing your {providerName} authentication</p>
      </div>
    </div>
  );
};

export default SocialAuthCallback;
